import { useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";

// Components
import Login from "./Components/Login";
import Signup from "./Components/Signup";
import ForgotPassword from "./Components/ForgotPassword";
import ResetPassword from "./Components/ResetPassword";
import GuestLanding from "./Components/GuestLanding";
import PublicApartments from "./Components/PublicApartments";
import PublicApartmentDetails from "./Components/PublicApartmentDetails";
import RequirementsPage from "./Components/RequirementsPage";
import ContactPage from "./Components/ContactPage";
import Home from "./Components/Home"; // Admin dashboard
import AdminLayout from "./Components/AdminLayout";
import TenantHome from "./Components/TenantHome"; // Tenant dashboard
import TenantLayout from "./Components/TenantLayout";
import TenantAvailableApartments from "./Components/TenantAvailableApartments";
import TenantApartmentDetails from "./Components/TenantApartmentDetails";
import TenantRentalApplication from "./Components/TenantRentalApplication";
import TenantApplications from "./Components/TenantApplications";

// Properties
import AddProperty from "./Components/AddProperty";
import EditProperty from "./Components/EditProperty";
import PropertyList from "./Components/PropertyList";
import MapProperties from "./Components/MapProperties";

// Tenants
import TenantList from "./Components/TenantList";
import AddTenant from "./Components/AddTenant";
import EditTenant from "./Components/EditTenant";

// Reports
import AdminReports from "./Components/AdminReports";
import AdminNeedsAttention from "./Components/AdminNeedsAttention";
import AdminActivityTimeline from "./Components/AdminActivityTimeline";
import AdminArchiveCenter from "./Components/AdminArchiveCenter";
import AdminPaymentStatements from "./Components/AdminPaymentStatements";
import AdminReminderCenter from "./Components/AdminReminderCenter";
import SubmitReport from "./Components/SubmitReport";
import AdminAnnouncements from "./Components/AdminAnnouncements";
import TenantAnnouncements from "./Components/TenantAnnouncements";
import MyRentals from "./Components/MyRentals";
import TenantProfile from "./Components/TenantProfile";
import TenantReportHistory from "./Components/TenantReportHistory";
import NotificationCenter from "./Components/NotificationCenter";
import AdminMessages from "./Components/AdminMessages";
import AdminLeadsInbox from "./Components/AdminLeadsInbox";
import TenantMonthlyStatement from "./Components/TenantMonthlyStatement";
import TenantMessages from "./Components/TenantMessages";

// Payments
import TenantPaymentForm from "./Components/TenantPaymentForm";
import PaymentHistory from "./Components/PaymentHistory";
import AdminPaymentList from "./Components/AdminPaymentList";

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(() => localStorage.getItem("loggedIn") === "true");
  const [role, setRole] = useState(() => localStorage.getItem("role") || "");

  const handleLogin = (userRole) => {
    localStorage.setItem("loggedIn", "true");
    localStorage.setItem("role", userRole);
    setIsLoggedIn(true);
    setRole(userRole);
  };

  const handleLogout = () => {
    localStorage.clear();
    setIsLoggedIn(false);
    setRole("");
  };

  const adminRoutes =
    isLoggedIn && role === "admin" ? (
      <AdminLayout onLogout={handleLogout} />
    ) : (
      <Navigate to="/" replace />
    );

  const tenantRoutes =
    isLoggedIn && role === "tenant" ? (
      <TenantLayout onLogout={handleLogout} />
    ) : (
      <Navigate to="/" replace />
    );

  return (
    <Router>
      <Routes>
        {/* Guest Landing */}
        <Route
          path="/"
          element={
            isLoggedIn ? (
              role === "admin" ? <Navigate to="/home" replace /> : <Navigate to="/tenant/home" replace />
            ) : (
              <GuestLanding />
            )
          }
        />

        {/* Login */}
        <Route
          path="/login"
          element={
            isLoggedIn ? (
              role === "admin" ? <Navigate to="/home" replace /> : <Navigate to="/tenant/home" replace />
            ) : (
              <Login onLogin={handleLogin} />
            )
          }
        />

        {/* Sign Up */}
        <Route
          path="/signup"
          element={
            isLoggedIn ? (
              role === "admin" ? <Navigate to="/home" replace /> : <Navigate to="/tenant/home" replace />
            ) : (
              <Signup onLogin={handleLogin} />
            )
          }
        />

        {/* Password Reset */}
        <Route
          path="/forgot-password"
          element={
            isLoggedIn ? (
              role === "admin" ? <Navigate to="/home" replace /> : <Navigate to="/tenant/home" replace />
            ) : (
              <ForgotPassword />
            )
          }
        />
        <Route
          path="/reset-password"
          element={
            isLoggedIn ? (
              role === "admin" ? <Navigate to="/home" replace /> : <Navigate to="/tenant/home" replace />
            ) : (
              <ResetPassword />
            )
          }
        />
        <Route
          path="/reset-password/:token"
          element={
            isLoggedIn ? (
              role === "admin" ? <Navigate to="/home" replace /> : <Navigate to="/tenant/home" replace />
            ) : (
              <ResetPassword />
            )
          }
        />

        {/* Public Rental Pages */}
        <Route path="/apartments" element={<PublicApartments />} />
        <Route path="/apartments/:id" element={<PublicApartmentDetails />} />
        <Route path="/requirements" element={<RequirementsPage />} />
        <Route path="/contact" element={<ContactPage />} />

        {/* Admin Pages */}
        <Route element={adminRoutes}>
          <Route path="/home" element={<Home />} />
          <Route path="/admin/viewings" element={<Navigate to="/admin/leads" replace />} />
          <Route path="/admin/reports" element={<AdminReports />} />
          <Route path="/admin/needs-attention" element={<AdminNeedsAttention />} />
          <Route path="/admin/activity" element={<AdminActivityTimeline />} />
          <Route path="/admin/archive-center" element={<AdminArchiveCenter />} />
          <Route path="/admin/management-report" element={<Navigate to="/home" replace />} />
          <Route path="/admin/payment-statements" element={<AdminPaymentStatements />} />
          <Route path="/admin/reminders" element={<AdminReminderCenter />} />
          <Route path="/admin/notifications" element={<NotificationCenter />} />
          <Route path="/admin/messages" element={<AdminMessages />} />
          <Route path="/admin/leads" element={<AdminLeadsInbox />} />
          <Route path="/add-property" element={<AddProperty />} />
          <Route path="/edit-property/:id" element={<EditProperty />} />
          <Route path="/list-properties" element={<PropertyList />} />
          <Route path="/map-properties" element={<MapProperties />} />
          <Route path="/admin/announcements" element={<AdminAnnouncements />} />
          <Route path="/admin/tenants" element={<TenantList />} />
          <Route path="/admin/tenants/add" element={<AddTenant />} />
          <Route path="/admin/tenants/edit/:id" element={<EditTenant />} />
        </Route>

        {/* Tenant Pages */}
        <Route element={tenantRoutes}>
          <Route path="/tenant/home" element={<TenantHome />} />
          <Route path="/tenant/apartments" element={<TenantAvailableApartments />} />
          <Route path="/tenant/apartments/:unitId" element={<TenantApartmentDetails />} />
          <Route path="/tenant/apply/:unitId" element={<TenantRentalApplication />} />
          <Route path="/tenant/applications" element={<TenantApplications />} />
          <Route path="/tenant/profile" element={<TenantProfile />} />
          <Route path="/my-rentals" element={<MyRentals />} />
          <Route path="/announcements" element={<TenantAnnouncements />} />
          <Route path="/tenant/notifications" element={<NotificationCenter />} />
          <Route path="/tenant/messages" element={<TenantMessages />} />
          <Route path="/submit-report" element={<SubmitReport />} />
          <Route path="/my-reports" element={<TenantReportHistory />} />
          <Route path="/payment-history" element={<PaymentHistory />} />
          <Route path="/tenant/monthly-statement" element={<TenantMonthlyStatement />} />
        </Route>

        {/* Payments */}
        <Route
          path="/payments"
          element={
            isLoggedIn ? (
              role === "tenant" ? (
                <TenantLayout onLogout={handleLogout}>
                  <TenantPaymentForm />
                </TenantLayout>
              ) : role === "admin" ? (
                <AdminLayout onLogout={handleLogout}>
                  <AdminPaymentList />
                </AdminLayout>
              ) : null
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
